import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import NavBar from "./components/NavBar";
import { AuthProvider } from "./components/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import PermissionProtectedRoute, { AdminRoute, VendorRoute } from "./components/PermissionProtectedRoute";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NewPage from "./pages/NewPage";
import CallsPage from "./pages/CallsPage";
import FormPage from "./pages/FormPage";
import NewFormPage from "./pages/NewFormPage";
import ViewFormPage from "./pages/ViewFormPage";
import EditFormPage from "./pages/EditFormPage";
import ServiceProviderRFP from "./pages/ServiceProviderRFP";
import ShorexForm from "./pages/ShorexForm";
import SharepointForm from "./components/SharepointForm";
import VendorPendingApprovalPage from "./pages/VendorPendingApprovalPage";
import VendorPendingApprovalItemsPage from "./pages/VendorPendingApprovalItemsPage";
import ServiceConfirmationPage from "./pages/ServiceConfirmationPage";
import ShoreExcursionAdmin from "./pages/ShoreExcursionAdmin";
import UserManagementPage from "./pages/UserManagementPage";
import VendorManagement from "./components/VendorManagement";
import RBACConfigPage from "./pages/RBACConfigPage";
import HomePage from "./pages/HomePage";
import { PERMISSIONS } from "./utils/rbac";

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('theme')) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className={`flex ${isDarkMode ? 'dark' : ''}`}>
          <NavBar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
          <div className="flex-1 sm:ml-64 mt-16 sm:mt-0 bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            <Routes>
              <Route path="/" element={<HomePage />} />
              
              {/* Calendar & Calls */}
              <Route path="/calendar" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_CALENDAR]}>
                  <CallsPage />
                </PermissionProtectedRoute>
              } />
              
              {/* Forms */}
              <Route path="/forms" element={<ProtectedRoute><h1>Forms</h1></ProtectedRoute>} />
              <Route path="/form/:id" element={<ProtectedRoute><FormPage /></ProtectedRoute>} />
              <Route path="/form/:id/:type/new" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.CREATE_FORMS]}>
                  <NewFormPage />
                </PermissionProtectedRoute>
              } />
              <Route path="/form/:id/:type/view" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_FORMS, PERMISSIONS.VIEW_OWN_FORMS]} requireAll={false}>
                  <ViewFormPage />
                </PermissionProtectedRoute>
              } />
              <Route path="/form/:id/:type/edit" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.EDIT_FORMS]} checkOwnership={true}>
                  <EditFormPage />
                </PermissionProtectedRoute>
              } />
              
              {/* Shore Excursions */}
              <Route path="/shorex/:id" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SHOREX, PERMISSIONS.CREATE_SHOREX]} requireAll={false}>
                  <ShorexForm />
                </PermissionProtectedRoute>
              } />
              
              {/* Vendor Management */}
              <Route path="/vendor-pending-approval" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_PENDING_APPROVALS]}>
                  <VendorPendingApprovalPage />
                </PermissionProtectedRoute>
              } />
              <Route path="/vendor-approval/:callId/:type" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.APPROVE_VENDORS]}>
                  <VendorPendingApprovalItemsPage />
                </PermissionProtectedRoute>
              } />
              
              {/* User Management - New Routes */}
              <Route path="/users" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS]} requireAll={false}>
                  <UserManagementPage />
                </PermissionProtectedRoute>
              } />
              <Route path="/vendor-management" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_VENDORS, PERMISSIONS.VIEW_VENDORS]} requireAll={false}>
                  <VendorManagement />
                </PermissionProtectedRoute>
              } />
              <Route path="/rbac-config" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.SYSTEM_CONFIG]} requireAll={false}>
                  <RBACConfigPage />
                </PermissionProtectedRoute>
              } />
              
              {/* Service Confirmation */}
              <Route path="/service-confirmation" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_SERVICE_CONFIRMATIONS]}>
                  <ServiceConfirmationPage />
                </PermissionProtectedRoute>
              } />
              
              {/* RFP & Bidding */}
              <Route path="/rfp" element={<ServiceProviderRFP />} />
              <Route path="/rfp-admin" element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_RFP_ADMIN]}>
                  <ShoreExcursionAdmin />
                </PermissionProtectedRoute>
              } />
              
              {/* Public Routes */}
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/new" element={<NewPage />} />
              
              {/* SharePoint Form */}
              <Route path="/sharepoint" element={
                <ProtectedRoute>
                  <SharepointForm
                    siteId="cruisesaudi.sharepoint.com,4143b01e-bd0a-4f72-ae18-1334fba60696,3dc58629-6e03-4061-8795-f3f6d1914775"
                    listId="01c32460-a2e3-4902-a43b-88a46ffa1f81"
                    excludedFields={['Title', 'Description_x0020_Item_x003a__x0']}
                    headerText={{
                      ShipName: "Ship Name",
                      CruiseLine: "Cruise Line",
                      Berthnumber: "Berth Number",
                      Nextportofcall: "Next Port of Call",
                      TypeofCall: "Type of Call",
                      Allpaxcrewonboard: "All Pax Crew Onboard",
                      ETA_x0028_Pilotstation_x0029_: 'ETA (Pilotstation)',
                      Reportpreparedon_x0028_date_x0029_: "Report Prepared On",
                      ETD_x0028_Berth_x0029_: "ETD (Berth)",
                    }}
                    itemId="23"
                  />
                </ProtectedRoute>
              } />
              
              <Route path="*" element={<h1>404 Not Found</h1>} />
            </Routes>
          </div>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;